import { Injectable, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { BehaviorSubject, ReplaySubject, combineLatest, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, pairwise, takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { prettyPlayerId } from 'app/core/utilities/user-util';
import { ModalSimpleMessageService } from 'app/shared/modals/modal-simple-message.service';
import { AppState } from 'app/types';
import assertNever from 'app/utils/assert-never';
import { Loggable } from 'app/utils/logging/loggable';
import User from 'entity/User';
import { InteractionMode, Presentation, Presenter, SocialState } from 'messages/websocket.messages';

import {
  DEFAULT_IN_GROUP_MUSIC,
  DEFAULT_MUSIC_VOLUME_TOAST,
  DEFAULT_PARTICIPANT_MAX_VOLUME_PRESENTATION,
  DEFAULT_PARTICIPANT_MAX_VOLUME_TOAST,
} from '../constants/volume';
import { Player } from '../social-pixi-canvas/player';
import SocialStateMachine from '../social-state-machine/social-state-machine';
import { LARGE_ROOM_SIZE } from '../social-state-machine/social-state.constants';
import SoundControl from '../sound-control';
import { socialAppSelectors } from '../state/app';

import { SceneService } from './scene.service';
import { VideoRoomService } from './video-room.service';
import { GroupShareScreenService } from './share-screen-group/group-share-screen.service';

const SUBSCRIPTION_WARNING_PARTICIPANT_COUNT = LARGE_ROOM_SIZE;

enum WarningState {
  NOT_SHOWN,
  VISIBLE,
  SHOWN,
}

/**
 * MultimediaRoomService manages audio/video changes as a result of updates to the room, particularly
 * resulting from changes in the room and its social state.
 * This is paired with MultimediaUserService which owns the business logic for handling users actions
 * to manage audio/video.
 */
@Injectable({
  providedIn: 'root',
})
export class MultimediaRoomService extends Loggable implements OnDestroy {
  private videoRoomService = inject(VideoRoomService);
  private sceneService = inject(SceneService);
  private modalService = inject(ModalSimpleMessageService);
  private groupShareScreenService = inject(GroupShareScreenService);
  private store = inject<Store<AppState>>(Store);

  private user?: User;
  private presentationSubscriptionWarning = false;
  private warningState = WarningState.NOT_SHOWN;
  private pendingVolume = 0;
  private usingPresentationSettings = false;
  private enableAudioRange = false;

  // Presentations and Events
  private isEventActive = false;
  private isPresentationActive = false;
  private presenterIds: string[] = [];
  private toaster: Presenter | undefined = undefined;
  private speakingParticipantIds: string[] = [];
  private presentation?: Presentation;
  private presenterCanHearAudience = true;
  private allowGroupConversations = true;
  private activeAnnouncement = false;
  private isConvoInBanner = false;

  /** Event tracking */
  private lastIsAnyEventHappening = false;
  private lastPresenterIds: string[] = [];
  private lastSpeakingParticipantIds: string[] = [];
  private lastPresenterCanHearAudience = true;
  private destroyed$ = new ReplaySubject<boolean>(1);

  public audienceCanUnmute$ = new BehaviorSubject<boolean>(true);
  private isToastActive = false;

  private soundControl = SoundControl.getInstance(this.sceneService);

  private mingleMode2 = false;

  public constructor() {
    super();
    this.setDisplayName('MultimediaRoomService');

    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => (this.user = user));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_settings)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((usingPresentationSettings) => (this.usingPresentationSettings = usingPresentationSettings));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.mingle_mode_2)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((mingleMode2) => (this.mingleMode2 = mingleMode2));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_presentation_subscription_warning)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (presentationSubscriptionWarning) => (this.presentationSubscriptionWarning = presentationSubscriptionWarning),
      );

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_audio_range)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enableAudioRange) => (this.enableAudioRange = enableAudioRange));

    this.store
      .select(socialAppSelectors.getPresentation)
      .pipe(pairwise(), takeUntil(this.destroyed$))
      .subscribe(([previous, presentation]) => {
        this.presentation = presentation;
        const { settings } = this.presentation ?? {};
        this.allowGroupConversations = settings?.allowGroupConversations ?? true;
        this.presenterCanHearAudience = settings?.interactionMode === InteractionMode.OPEN;

        // Restore music volume if presentation is not active
        if (presentation) {
          this.adjustInProgressEventsMusic(true);
        }
        if (previous && !presentation) {
          this.resetSoundForPresentationEnd();
        }
      });

    this.store
      .select(socialAppSelectors.isEventActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: async (active) => {
          this.isEventActive = active;
          this.adjustInProgressEventsMusic(active);
          await this.makeEventStateChecks(active);
        },
      });

    this.store
      .select(socialAppSelectors.getMyGroupScreenShare)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((myGroupScreenShare) => {
        if (myGroupScreenShare?.groupId) {
          this.soundControl.reduceMusicVolume(0, 'fade');
          return;
        } else {
          this.soundControl.restoreMusicVolumeAfterEvent();
        }
      });

    this.store
      .select(socialAppSelectors.getSpeakingParticipantIds)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((speakingParticipants) => {
        this.speakingParticipantIds = speakingParticipants;
      });

    this.store
      .select(socialAppSelectors.getPresenterIds)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((presenterIds) => {
        this.presenterIds = presenterIds;
      });

    // Start and stop of icebreaker
    this.store
      .select(socialAppSelectors.getConvoUser)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((getConvoUser) => {
        this.toaster = getConvoUser;
        if (getConvoUser) {
          const convoUserPlayer = this.sceneService.sceneManager?.getPlayer(getConvoUser.userId);
          if (convoUserPlayer) {
            this.soundControl.setEventSpeakerVolume({ p: convoUserPlayer });
          }
        }
      });

    this.store
      .select(socialAppSelectors.isConvoInBanner)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((bannerPhase) => (this.isConvoInBanner = bannerPhase));

    this.store
      .select(socialAppSelectors.activeAnnouncement)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((activeAnnouncement) => (this.activeAnnouncement = activeAnnouncement));

    // Start and stop of toast
    combineLatest([
      this.store.select(socialAppSelectors.isToastActive).pipe(pairwise(), takeUntil(this.destroyed$)),
      this.store.select(socialAppSelectors.getToaster).pipe(takeUntil(this.destroyed$)),
    ]).subscribe(([[previous, isToastActive], toaster]) => {
      this.isToastActive = isToastActive;
      this.toaster = toaster;

      if (isToastActive && toaster) {
        const toasterPlayer = this.sceneService.sceneManager?.getPlayer(toaster.userId);
        if (toasterPlayer) {
          this.adjustSoundForToastStart();
          this.soundControl.setEventSpeakerVolume({ p: toasterPlayer });
        }
      } else if (previous && !isToastActive) {
        this.resetSoundForToastEnd();
      }
    });

    // Start and stop of icebreaker
    this.store
      .select(socialAppSelectors.isConvoInBanner)
      .pipe(pairwise(), takeUntil(this.destroyed$))
      .subscribe(([previous, isConvoInBanner]) => {
        if (previous && !isConvoInBanner) {
          this.resetSoundForToastEnd();
        }
      });

    this.store
      .select(socialAppSelectors.isJoined)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isJoined) => {
        if (isJoined) {
          this.sceneService.sceneManager?.sagQueue$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
            this.recalculateAllVolumeAndSubscriptions();
          });
        }
      });
  }

  public async setCrowdVolume(volume: number): Promise<void> {
    if (!SocialStateMachine.shared.state) {
      return;
    }

    if (
      !this.presentationSubscriptionWarning ||
      this.usingPresentationSettings ||
      this.sceneService.participantCount < SUBSCRIPTION_WARNING_PARTICIPANT_COUNT
    ) {
      this.updateNonPresenterAudioSettings(SocialStateMachine.shared.state, volume);
      return;
    }

    // If warnings are on, proceed through a state machine to handle showing the warning while retaining future slide
    // values while the user continues to drag the slider. This ensures we retain the final value and can use it if
    // the user confirms the volume change
    switch (this.warningState) {
      // Show the warning and wait for the users's confirmation or rejection.
      case WarningState.NOT_SHOWN:
        {
          this.pendingVolume = volume;
          this.warningState = WarningState.VISIBLE;
          const confirmed = await this.showSubscriptionWarningDialog();
          this.updateNonPresenterAudioSettings(SocialStateMachine.shared.state, confirmed ? this.pendingVolume : 0);
          this.warningState = WarningState.SHOWN;
        }
        return;

      // When visible, just remember the slide value
      case WarningState.VISIBLE:
        this.pendingVolume = volume;
        return;

      case WarningState.SHOWN:
        this.updateNonPresenterAudioSettings(SocialStateMachine.shared.state, volume);
        return;

      default:
        assertNever(this.warningState);
    }
  }

  private async showSubscriptionWarningDialog(): Promise<boolean> {
    const message = `There are over ${LARGE_ROOM_SIZE} people in the room. Streaming that many people at once may cause a suboptimal experience if you don’t have a strong machine. Are you sure you want to continue?`;
    const response$ = this.modalService.confirm({ title: 'Heads up...', message, actionButtonTitle: 'Yes, continue' });
    const res = await firstValueFrom(response$);
    return res;
  }

  public updateNonPresenterAudioSettings(state: SocialState, volumeToSet: number): boolean {
    // If we don't update the audio, return false so the upstream function knows not to
    // update internal variables
    if (!this.isPresentationActive) {
      return false;
    }
    this.soundControl.setParticipantMaxVolume(volumeToSet);
    if (!this.presenterCanHearAudience) {
      // If presenter can't hear audience, only allow volume change for speakingParticipants
      const audibleCrowdMembers = this.sceneService.sceneManager?.getParticipants(this.speakingParticipantIds);
      const setBy = 'updateNonPresenterAudioSettings speakingParticipantsOnly';

      audibleCrowdMembers?.forEach((p) => {
        // Note, setParticipantVolume includes logic to respect maximum volume
        this.soundControl.setParticipantVolume({
          p,
          playerIsAudience: true,
          volume: volumeToSet,
          setBy,
        });
      });
    } else {
      const crowdMembers = this.sceneService.sceneManager?.participantsList.filter(({ userId }): boolean => {
        return !this.presenterIds.includes(userId);
      });
      const setBy = 'updateNonPresenterAudioSettings all participants';

      crowdMembers?.forEach((p) => {
        // Note, setParticipantVolume includes logic to respect maximum volume
        this.soundControl.setParticipantVolume({
          p,
          playerIsAudience: true,
          volume: volumeToSet,
          setBy,
        });
      });
    }
    return true;
  }

  public resetParticipantSoundForToastInProgress(): void {
    this.soundControl.reset();
    this.setAllParticipantsSound();
  }

  // Reduce music volume and set participant max volume for toast start
  public adjustSoundForToastStart(): void {
    this.soundControl.reduceMusicVolume(DEFAULT_MUSIC_VOLUME_TOAST, 'fade');
    this.soundControl.setParticipantMaxVolume(DEFAULT_PARTICIPANT_MAX_VOLUME_TOAST);
    this.setAllParticipantsSound();
  }

  public adjustSoundForPresentationStart(): void {
    if (!SocialStateMachine.shared.state) {
      return;
    }

    if (!this.presentation?.settings) {
      return this.error('Missing presentation settings');
    }

    this.soundControl.reduceMusicVolume(0, 'fade');

    const participantMaxVolume =
      this.presentation?.settings.interactionMode === InteractionMode.NONE
        ? 0
        : DEFAULT_PARTICIPANT_MAX_VOLUME_PRESENTATION;
    this.soundControl.setParticipantMaxVolume(participantMaxVolume);
  }

  // Restores music volume and sets participant max volume for presentation end
  public resetSoundForToastEnd(): void {
    this.soundControl.reset();
    this.soundControl.restoreMusicVolumeAfterEvent();
    this.setAllParticipantsSound();
  }

  // Restores music volume and sets participant max volume for presentation end
  private resetSoundForPresentationEnd(): void {
    this.soundControl.reset();
    this.soundControl.restoreMusicVolumeAfterEvent();
    this.setAllParticipantsSound();
  }

  public setAllVideoParticipantsSound(player?: Player): void {
    const { sceneManager } = this.sceneService;
    const mainPlayer = player ? player : sceneManager?.player;
    if (!sceneManager || !mainPlayer) {
      return;
    }

    const participants = sceneManager.participantsList.filter((p) => !!this.videoRoomService.getParticipant(p.userId));

    this.setSoundByRoleAndDistance({
      participants,
      mainPlayer,
      setBy: `setAllVideoParticipantsSound`,
    });
  }

  // NOTE similarity to makeEventStateChecks in subscription-controller.
  // TODO: better centralize similar checks & updates between volume & subscription controls
  /** Methods for managing subscriptions & sound during Presentation SocialEvents */
  private async makeEventStateChecks(eventActive: boolean): Promise<void> {
    if (eventActive !== this.lastIsAnyEventHappening) {
      this.lastIsAnyEventHappening = eventActive;
      await this.updateSpeakers();
      this.updateWholeRoomAudioSettings();
    } else if (eventActive) {
      // Check for changes in speakers and audio settings for ongoing event
      await this.updateSpeakers();
      this.updateWholeRoomAudioSettings();
    }
  }

  /**
   * Adjusts the music if an event is in progress based on its specific business rules.
   * @param state The state to evaluate
   */
  private adjustInProgressEventsMusic(eventActive: boolean): void {
    if (eventActive) {
      this.soundControl.reduceMusicVolume(0, 'fade');
    }
  }

  private async updateSpeakers() {
    if (!this.user?.userId) {
      return this.warn('userId not defined on update speakers');
    }
    // if current user is new presenter or newly not a presenter, recalculate the whole room
    const userIsCurrentPresenter = this.presenterIds.includes(this.user.userId);
    const userWasLastPresenter = this.lastPresenterIds.includes(this.user.userId);
    if (userIsCurrentPresenter !== userWasLastPresenter) {
      return this.recalculateAllVolumeAndSubscriptions();
    }

    if (!isEqual(this.presenterIds, this.lastPresenterIds)) {
      const exPresenters = this.lastPresenterIds.filter((id) => !this.presenterIds.includes(id));
      const newPresenters = this.presenterIds.filter((id) => !this.lastPresenterIds.includes(id));
      await this.recalculateSubscriptionsById({
        playerIds: [...exPresenters, ...newPresenters],
        setBy: 'presenters check',
      });
      this.lastPresenterIds = this.presenterIds;
    }

    if (!isEqual(this.speakingParticipantIds, this.lastSpeakingParticipantIds)) {
      const exSpeakingParticipants = this.lastSpeakingParticipantIds.filter(
        (id) => !this.speakingParticipantIds.includes(id),
      );
      const newSpeakingParticipants = this.speakingParticipantIds.filter(
        (id) => !this.lastSpeakingParticipantIds.includes(id),
      );
      await this.recalculateSubscriptionsById({
        playerIds: [...exSpeakingParticipants, ...newSpeakingParticipants],
        setBy: 'speaking participants check',
      });
      this.lastSpeakingParticipantIds = this.speakingParticipantIds;
    }
  }

  private updateWholeRoomAudioSettings(): void {
    if (this.allowGroupConversations !== this.audienceCanUnmute$.value) {
      this.audienceCanUnmute$.next(this.allowGroupConversations);
      this.recalculateAllVolumeAndSubscriptions();
    }

    if (this.presenterCanHearAudience !== this.lastPresenterCanHearAudience) {
      this.lastPresenterCanHearAudience = this.presenterCanHearAudience;
      this.recalculateAllVolumeAndSubscriptions();
    }
  }

  private setSoundByRoleAndDistance({
    /** The current user's player */
    mainPlayer,
    /** The users we're setting sound for */
    participants,
    setBy,
  }: {
    mainPlayer?: Player;
    participants: Player[];
    setBy: string;
  }): void {
    // Ensure all players are defined and are not the main player
    if (!mainPlayer) {
      return this.warn(`Volume set attempted without mainPlayer by ${setBy}`);
    }
    const playersToSet = participants?.filter((p) => p && p.userId !== mainPlayer.userId) || [];
    if (playersToSet.length < 1) {
      return;
    }

    // No Active Event
    if (!this.isEventActive || this.isConvoInBanner) {
      playersToSet.forEach((p) => {
        const playerId = prettyPlayerId(p.user);
        const enableAudioRange = this.enableAudioRange;
        const volume = this.soundControl.getPlayerVolumeByDistance({ enableAudioRange, mainPlayer, p });

        // Players in a group mingle mode 2.0
        if (
          this.mingleMode2 &&
          mainPlayer.groupId &&
          mainPlayer.groupId === p.groupId &&
          !mainPlayer.groupWarning &&
          !p.groupWarning
        ) {
          this.soundControl.setParticipantVolume({
            p,
            volume: volume,
            playerIsAudience: true,
            setBy: `${setBy} set: non-event distance in a group update for ${playerId}`,
          });
          return;
        }

        const isDifferentGroup = mainPlayer.groupId !== p.groupId;
        const hasGroupWarning = mainPlayer.groupWarning || p.groupWarning;
        const areRegularMembers = !p.isGroupSag && !mainPlayer.isGroupSag;

        if (this.mingleMode2 && (isDifferentGroup || (areRegularMembers && hasGroupWarning))) {
          const outOfGroupVolume = 0;

          this.soundControl.setParticipantVolume({
            p,
            volume: outOfGroupVolume,
            playerIsAudience: true,
            setBy: `${setBy} set: non-event distance out of group update for ${playerId}`,
          });
          return;
        }

        this.soundControl.setParticipantVolume({
          p,
          volume,
          setBy: `${setBy} set: non-event distance update for ${playerId}`,
        });
      });
      return;
    }

    // Active Event
    const mainPlayerIsPresenter = this.presenterIds.includes(mainPlayer.userId);
    playersToSet.forEach((p) => {
      const { userId } = p;
      const playerId = prettyPlayerId(p.user);
      const enableAudioRange = this.enableAudioRange;
      const volume = this.soundControl.getPlayerVolumeByDistance({ enableAudioRange, mainPlayer, p });

      // Player to set is a toaster, presenter or speaking participant
      if (
        this.presenterIds.includes(userId) ||
        this.speakingParticipantIds.includes(userId) ||
        // handling this elsewhere, remove?
        (this.toaster?.userId === userId && this.isToastActive)
      ) {
        this.soundControl.setEventSpeakerVolume({ p });
        return;
      }

      // Player to set is a participant while someone else is toasting/announcing convo
      if (this.activeAnnouncement) {
        const nonToasterVolume = Math.min(volume, DEFAULT_PARTICIPANT_MAX_VOLUME_TOAST);
        this.soundControl.setParticipantVolume({
          p,
          volume: nonToasterVolume,
          setBy: `${setBy} set: toast/ice breaker in progress, participant ${playerId} not toaster`,
        });
        return;
      }

      // Player to set is a participant of the current user's presentation
      if (this.isPresentationActive && mainPlayerIsPresenter) {
        const audienceVolume = this.presenterCanHearAudience ? volume : 0;
        this.soundControl.setParticipantVolume({
          p,
          volume: audienceVolume,
          playerIsAudience: true,
          setBy: `${setBy} set: audience player ${playerId} volume relative to presenter`,
        });
        return;
      }

      // Player is an audience member
      if (this.isPresentationActive) {
        const audienceVolume = this.allowGroupConversations ? volume : 0;
        this.soundControl.setParticipantVolume({
          p,
          volume: audienceVolume,
          playerIsAudience: true,
          setBy: `${setBy} set: set volume between non-speaking participant ${playerId}`,
        });
        return;
      }

      // For all other cases, restore the volume to the proper group and distance based value.
      this.soundControl.setParticipantVolume({
        p,
        volume,
        playerIsAudience: true,
        setBy: `${setBy} set: set unhandled event-distance volume non-speaking participant ${playerId}`,
      });
    });
  }

  private recalculateAllVolumeAndSubscriptions(): void {
    this.setAllParticipantsSound();
  }

  private setAllParticipantsSound(player?: Player): void {
    const { sceneManager } = this.sceneService;
    const mainPlayer = player ? player : sceneManager?.player;
    if (!sceneManager || !mainPlayer) {
      return;
    }

    const participants = sceneManager.participantsList;
    this.setSoundByRoleAndDistance({
      participants,
      mainPlayer,
      setBy: `setAllParticipantsSound`,
    });
  }

  private async recalculateSubscriptionsById({
    forcedMainPlayer,
    playerIds,
    setBy,
  }: {
    playerIds: string[];
    forcedMainPlayer?: Player;
    setBy: string;
  }): Promise<void> {
    if (playerIds.length < 1) {
      return;
    }
    const mainPlayer = forcedMainPlayer
      ? forcedMainPlayer
      : await this.sceneService?.sceneManager?.getPlayerAsync(this.user?.userId);
    if (!mainPlayer) {
      return this.warn(
        'recalculateSubscriptionsById ran before we have a mainPlayer. Scene manager available?: ',
        !!this.sceneService.sceneManager,
      );
    }
    // Participants to Recalculate Sound
    const participants = this.sceneService?.sceneManager?.getParticipants(playerIds);
    if (!participants) {
      return;
    }

    this.setSoundByRoleAndDistance({
      participants,
      mainPlayer,
      setBy: `${setBy} recalculateSubscriptionsById`,
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
